import { fetchJson } from "../lib/api";

export type MemberRole = "admin" | "member";
export type AuthorizationModel = "legacy" | "v2";
export type RolePreset =
  | "viewer"
  | "purchasing"
  // Controlling absorbed the former "accounting" preset (#538).
  | "controlling"
  | "administrator";

export type Member = {
  user_id: string;
  email: string | null;
  display_name: string | null;
  role: MemberRole;
  created_at: string | null;
  is_self: boolean;
  role_presets: RolePreset[];
};

export type MemberList = {
  authorization_model: AuthorizationModel;
  total: number;
  items: Member[];
};

export async function listMembers(): Promise<MemberList> {
  return parseMemberList(await fetchJson<unknown>("/api/members"));
}

export async function addMember(
  email: string,
  role: MemberRole,
  rolePresets?: RolePreset[],
): Promise<{
  user_id: string;
  email: string | null;
  role: MemberRole;
  authorization_model: AuthorizationModel;
  role_presets: RolePreset[];
}> {
  return fetchJson("/api/members", {
    method: "POST",
    body: JSON.stringify({
      email,
      role,
      ...(rolePresets ? { role_presets: rolePresets } : {}),
    }),
  });
}

export async function changeMemberRole(
  userId: string,
  role: MemberRole,
): Promise<{ user_id: string; role: MemberRole }> {
  return fetchJson(`/api/members/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

export async function removeMember(
  userId: string,
): Promise<{ user_id: string; removed: boolean }> {
  return fetchJson(`/api/members/${encodeURIComponent(userId)}`, {
    method: "DELETE",
  });
}

export async function replaceMemberRolePresets(
  userId: string,
  rolePresets: RolePreset[],
): Promise<{
  user_id: string;
  authorization_model: "v2";
  role_presets: RolePreset[];
}> {
  return fetchJson(
    `/api/members/${encodeURIComponent(userId)}/role-presets`,
    {
      method: "PUT",
      body: JSON.stringify({ role_presets: rolePresets }),
    },
  );
}

const ROLE_PRESET_KEYS = new Set<RolePreset>([
  "viewer",
  "purchasing",
  "controlling",
  "administrator",
]);

function parseMemberList(value: unknown): MemberList {
  if (!isRecord(value) || !Array.isArray(value.items)) {
    throw memberContractError();
  }
  const authorizationModel = value.authorization_model;
  const total = value.total;
  if (
    (authorizationModel !== "legacy" && authorizationModel !== "v2") ||
    !Number.isInteger(total) ||
    Number(total) < 0
  ) {
    throw memberContractError();
  }
  const items = value.items.map(parseMember);
  if (Number(total) !== items.length) throw memberContractError();
  return {
    authorization_model: authorizationModel,
    total: Number(total),
    items,
  };
}

function parseMember(value: unknown): Member {
  if (!isRecord(value)) throw memberContractError();
  const userId = nonEmptyString(value.user_id);
  const email = nullableString(value.email);
  const displayName = nullableString(value.display_name);
  const createdAt = nullableString(value.created_at);
  const role = value.role;
  if (
    !userId ||
    email === undefined ||
    displayName === undefined ||
    createdAt === undefined ||
    (role !== "admin" && role !== "member") ||
    typeof value.is_self !== "boolean" ||
    !Array.isArray(value.role_presets)
  ) {
    throw memberContractError();
  }
  const rolePresets = value.role_presets.map((preset) => {
    if (typeof preset !== "string" || !ROLE_PRESET_KEYS.has(preset as RolePreset)) {
      throw memberContractError();
    }
    return preset as RolePreset;
  });
  if (
    rolePresets.length === 0 ||
    new Set(rolePresets).size !== rolePresets.length ||
    (rolePresets.includes("viewer") && rolePresets.length !== 1)
  ) {
    throw memberContractError();
  }
  return {
    user_id: userId,
    email,
    display_name: displayName,
    role,
    created_at: createdAt,
    is_self: value.is_self,
    role_presets: rolePresets,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function nullableString(value: unknown): string | null | undefined {
  return value === null || typeof value === "string" ? value : undefined;
}

function memberContractError(): Error {
  return new Error("Die Benutzer- und Rollenliste ist unvollständig.");
}
